export const FLUSS_NAMESPACE = "http://primum.cloud/schema/fluss";

// Keep in sync with Fluss::Compiler::SUPPORTED (app/backend/app/services/fluss/compiler.rb) —
// those are the only bpmn:* node types the compiler recognizes; anything else fails compilation
// with an "unsupported_type" error. The Studio canvas palette and automation panel both derive
// their restrictions from this single map so the two can't silently drift apart.
//
// parallelGateway/inclusiveGateway (Fase 9/A2-A3) have no dedicated one-click palette button
// (FlussCanvas.tsx's RestrictedPaletteProvider still only registers the original five) — they're
// reached via bpmn-js's native replace menu on an existing gateway shape, or the
// bpmn-js-create-append-anything popup, same as the rest of the full BPMN catalog. They still
// belong here (not in FLUSS_DESIGN_NODE_TYPES) because the compiler executes them.
export const FLUSS_NODE_TYPES = {
    startEvent: "bpmn:StartEvent",
    endEvent: "bpmn:EndEvent",
    userTask: "bpmn:UserTask",
    manualTask: "bpmn:ManualTask",
    serviceTask: "bpmn:ServiceTask",
    businessRuleTask: "bpmn:BusinessRuleTask",
    scriptTask: "bpmn:ScriptTask",
    sendTask: "bpmn:SendTask",
    exclusiveGateway: "bpmn:ExclusiveGateway",
    parallelGateway: "bpmn:ParallelGateway",
    inclusiveGateway: "bpmn:InclusiveGateway",
    eventBasedGateway: "bpmn:EventBasedGateway",
    callActivity: "bpmn:CallActivity",
} as const;

export const FLUSS_SUPPORTED_NODE_TYPES = Object.values(FLUSS_NODE_TYPES);

// Lote 1 (AJUSTE 003a/ADR-23), revised per the architect's plan §2: the FIXED palette now mirrors
// bpmn-js's own stock 14-entry PaletteProvider (generic types, same as demo.bpmn.io) instead of a
// hand-curated list — the full BPMN catalog (every gateway/task/event variant) is reachable via
// the bpmn-js-create-append-anything popup (`n`/`a`) registered in FlussCanvas.tsx, not as
// dedicated palette buttons. These are NOT added to FLUSS_NODE_TYPES/FLUSS_SUPPORTED_NODE_TYPES on
// purpose — Fluss::Compiler::SUPPORTED still only recognizes the executable types above, so
// publishing a definition that uses one of these fails with a clear "unsupported_type" error
// naming the element (already enforced server-side, unaffected by this revision); saving a draft
// that uses them is fine (documentation-only, per ADR-23 "desenho amplo, execução restrita").
// Manual Task is executable and configured by the automation panel; unsupported catalog-only shapes stay here.
export const FLUSS_DESIGN_NODE_TYPES = {
    intermediateThrowEvent: "bpmn:IntermediateThrowEvent",
    task: "bpmn:Task",
    subProcess: "bpmn:SubProcess",
    dataObjectReference: "bpmn:DataObjectReference",
    dataStoreReference: "bpmn:DataStoreReference",
    group: "bpmn:Group",
} as const;

export const flussModdleDescriptor = {
    name: "Fluss",
    uri: FLUSS_NAMESPACE,
    prefix: "fluss",
    xml: { tagAlias: "lowerCase" },
    types: [
        {
            name: "Binding",
            superClass: ["Element"],
            properties: [
                { name: "kind", isAttr: true, type: "String" },
                { name: "recordSource", isAttr: true, type: "String" },
                { name: "replyToNode", isAttr: true, type: "String" },
                { name: "table", isAttr: true, type: "String" },
                { name: "column", isAttr: true, type: "String" },
                { name: "businessRule", isAttr: true, type: "String" },
                { name: "script", type: "Script" },
                { name: "capabilities", isAttr: true, type: "String" },
                { name: "timeout_ms", isAttr: true, type: "String" },
                { name: "abort_on_error", isAttr: true, type: "String" },
                { name: "actionId", isAttr: true, type: "String" },
                { name: "params", isAttr: true, type: "String" },
                { name: "assigneeRole", isAttr: true, type: "String" },
                { name: "assigneeUser", isAttr: true, type: "String" },
                { name: "assigneeExpression", isAttr: true, type: "String" },
                { name: "allowedOutcomes", isAttr: true, type: "String" },
                { name: "description", isAttr: true, type: "String" },
                { name: "taskTitle", isAttr: true, type: "String" },
                { name: "instructions", isAttr: true, type: "String" },
                { name: "target_table", isAttr: true, type: "String" },
                { name: "field_mappings", isAttr: true, type: "String" },
                { name: "completionMode", isAttr: true, type: "String" },
                { name: "completionCondition", isAttr: true, type: "String" },
                { name: "waitForCompletion", isAttr: true, type: "String" },
                { name: "waitCondition", isAttr: true, type: "String" },
                { name: "timeoutMinutes", isAttr: true, type: "String" },
                // Must be named exactly "templateCode" — FlussEngine::Handlers::UserTaskHandler
                // reads context.binding_for("notification")&.config&.[]("templateCode") verbatim,
                // with no key renaming anywhere in the compile/publish pipeline.
                { name: "templateCode", isAttr: true, type: "String" },
                { name: "signalName", isAttr: true, type: "String" },
                { name: "eventType", isAttr: true, type: "String" },
                { name: "correlationKey", isAttr: true, type: "String" },
                { name: "payload", isAttr: true, type: "String" },
                { name: "variableMappings", isAttr: true, type: "String" },
                { name: "scheduleType", isAttr: true, type: "String" },
                { name: "timeOfDay", isAttr: true, type: "String" },
                { name: "daysOfWeek", isAttr: true, type: "String" },
                { name: "daysOfMonth", isAttr: true, type: "String" },
                { name: "timezone", isAttr: true, type: "String" },
                { name: "cron", isAttr: true, type: "String" },
                { name: "mode", isAttr: true, type: "String" },
                { name: "durationMinutes", isAttr: true, type: "String" },
                { name: "date", isAttr: true, type: "String" },
                { name: "field", isAttr: true, type: "String" },
                { name: "offsetMinutes", isAttr: true, type: "String" },
                { name: "offsetDirection", isAttr: true, type: "String" },
                { name: "finalStatus", isAttr: true, type: "String" },
                { name: "notify", isAttr: true, type: "String" },
                { name: "templateId", isAttr: true, type: "String" },
                { name: "name", isAttr: true, type: "String" },
                { name: "valueSource", isAttr: true, type: "String" },
                { name: "recordUpdates", isAttr: true, type: "String" },
                { name: "triggerType", isAttr: true, type: "String" },
                { name: "condition", isAttr: true, type: "String" },
                { name: "formId", isAttr: true, type: "String" },
                { name: "formMode", isAttr: true, type: "String" },
                { name: "initialVariables", isAttr: true, type: "String" },
                { name: "linkAnchor", isAttr: true, type: "String" },
                { name: "enabled", isAttr: true, type: "String" },
                { name: "manualBusinessRule", isAttr: true, type: "String" },
                { name: "allowedRoles", isAttr: true, type: "String" },
                { name: "allowedUsers", isAttr: true, type: "String" },
                { name: "startInputs", isAttr: true, type: "String" },
                // Fase 10 (A4) boundary events. errorCodes/errorVariable: boundary_error.
                // businessHours/scheduleId/repeatIntervalMinutes/repeatMax: boundary_timer only
                // (Fluss::TimerBinding::ALIASES) — inert for timer_start/timer_catch.
                // workRecordDisposition applies to every boundary kind (Fluss::BoundaryBinding).
                // boundary_conditional reuses "table" and "condition" above, already present for
                // trigger/gateway bindings.
                { name: "errorCodes", isAttr: true, type: "String" },
                { name: "errorVariable", isAttr: true, type: "String" },
                { name: "businessHours", isAttr: true, type: "String" },
                { name: "scheduleId", isAttr: true, type: "String" },
                { name: "repeatIntervalMinutes", isAttr: true, type: "String" },
                { name: "repeatMax", isAttr: true, type: "String" },
                { name: "workRecordDisposition", isAttr: true, type: "String" },
                // Fase 12 (D1) call_activity.
                { name: "definitionKey", isAttr: true, type: "String" },
                { name: "versionPolicy", isAttr: true, type: "String" },
                { name: "pinnedVersion", isAttr: true, type: "String" },
                { name: "onChildFailure", isAttr: true, type: "String" },
                { name: "cascadeCancel", isAttr: true, type: "String" },
                { name: "inputMappings", isAttr: true, type: "String" },
                { name: "outputMappings", isAttr: true, type: "String" },
                // PRIM-PILOT-043 (ACH-032): "outputs do processo" declared on a plain end node
                // (kind="end"), a JSON array of variable names -- source of options for the Call
                // Activity panel's "Called process's variable" Autocomplete.
                { name: "processOutputs", isAttr: true, type: "String" },
                // PRIM-PILOT-058/ACH-035: design-only Data Object link; column/field/mappings already mean other binding contracts.
                { name: "variableRef", isAttr: true, type: "String" },
            ],
        },
        {
            name: "Script",
            superClass: ["Element"],
            properties: [
                { name: "body", isBody: true, type: "String" },
            ],
        },
        {
            name: "Config",
            superClass: ["Element"],
            properties: [
                { name: "description", isAttr: true, type: "String" },
                { name: "owner", isAttr: true, type: "String" },
                { name: "raci", isAttr: true, type: "String" },
                { name: "priority", isAttr: true, type: "String" },
                // inclusiveGateway (Fase 9/A3) opt-in escape hatch: "the conditions cover every
                // case, no default path needed" — read by Fluss::Compiler as a non-blocking
                // warning instead of the blocking gateway_missing_default error.
                { name: "exhaustive", isAttr: true, type: "String" },
            ],
        },
    ],
};

export const createEmptyFlussXml = (definitionKey: string, name: string) => {
    const processId = `Process_${definitionKey.replace(/[^A-Za-z0-9_]/g, "_")}`;
    const safeName = name
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    return `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
  xmlns:di="http://www.omg.org/spec/DD/20100524/DI"
  xmlns:fluss="${FLUSS_NAMESPACE}"
  id="Definitions_${processId}" targetNamespace="http://primum.cloud/fluss">
  <bpmn:process id="${processId}" name="${safeName}" isExecutable="true">
    <bpmn:startEvent id="StartEvent_1" name="Start">
      <bpmn:outgoing>Flow_1</bpmn:outgoing>
    </bpmn:startEvent>
    <bpmn:endEvent id="EndEvent_1" name="End">
      <bpmn:incoming>Flow_1</bpmn:incoming>
    </bpmn:endEvent>
    <bpmn:sequenceFlow id="Flow_1" sourceRef="StartEvent_1" targetRef="EndEvent_1" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="${processId}">
      <bpmndi:BPMNShape id="StartEvent_1_di" bpmnElement="StartEvent_1">
        <dc:Bounds x="180" y="160" width="36" height="36" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNShape id="EndEvent_1_di" bpmnElement="EndEvent_1">
        <dc:Bounds x="380" y="160" width="36" height="36" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNEdge id="Flow_1_di" bpmnElement="Flow_1">
        <di:waypoint x="216" y="178" />
        <di:waypoint x="380" y="178" />
      </bpmndi:BPMNEdge>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;
};

export const toDefinitionKey = (value: string) =>
    value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9_]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 80);

const BPMNDI_NAMESPACE = "http://www.omg.org/spec/BPMN/20100524/DI";
const DC_NAMESPACE = "http://www.omg.org/spec/DD/20100524/DC";
const DI_NAMESPACE = "http://www.omg.org/spec/DD/20100524/DI";

interface FlussLayoutNode { id: string; x: number; y: number; width: number; height: number }

export const studioDraftXml = (xml: string, definitionKey: string, name: string) =>
    /<bpmn:process\b/.test(xml) ? xml : createEmptyFlussXml(definitionKey, name);

export const ensureFlussDiagram = (xml: string) => {
    if (xml.includes("bpmndi:BPMNDiagram")) return xml;
    const processMatch = xml.match(/<bpmn:process\b[^>]*\bid="([^"]+)"/);
    if (!processMatch) return xml;
    // Fase 9/A2-A3: parallelGateway/inclusiveGateway are the first genuinely NEW bpmn:* tag
    // names since exclusiveGateway (Fase 4) — every node type added in between (timer/signal/
    // terminate/milestone) reuses startEvent/endEvent/intermediateCatchEvent/
    // intermediateThrowEvent, already covered here, so this pattern never needed touching for
    // them. Missing a tag here doesn't just mislay it visually — positions.get() below returns
    // undefined for it, so ANY edge touching it is silently dropped too (continue), which is
    // exactly the invisible-node-and-edge failure that motivated this fix.
    const nodePattern = /<bpmn:(startEvent|endEvent|userTask|manualTask|serviceTask|scriptTask|exclusiveGateway|parallelGateway|inclusiveGateway|intermediateCatchEvent|intermediateThrowEvent)\b[^>]*\bid="([^"]+)"/g;
    const nodes: FlussLayoutNode[] = [];
    let match: RegExpExecArray | null;
    while ((match = nodePattern.exec(xml))) {
        const index = nodes.length;
        const event = match[1].toLowerCase().includes("event");
        const gateway = match[1].toLowerCase().includes("gateway");
        nodes.push({
            id: match[2],
            x: 100 + (index % 4) * 210,
            y: 100 + Math.floor(index / 4) * 150,
            width: event ? 36 : gateway ? 50 : 120,
            height: event ? 36 : gateway ? 50 : 80,
        });
    }
    if (nodes.length === 0) return xml;
    const positions = new Map(nodes.map((node) => [node.id, node]));
    const shapes = nodes.map((node) => (
        `<bpmndi:BPMNShape id="${node.id}_di" bpmnElement="${node.id}"><dc:Bounds x="${node.x}" y="${node.y}" width="${node.width}" height="${node.height}" /></bpmndi:BPMNShape>`
    )).join("");
    const flowPattern = /<bpmn:sequenceFlow\b[^>]*\bid="([^"]+)"[^>]*\bsourceRef="([^"]+)"[^>]*\btargetRef="([^"]+)"/g;
    const edges: string[] = [];
    while ((match = flowPattern.exec(xml))) {
        const source = positions.get(match[2]);
        const target = positions.get(match[3]);
        if (!source || !target) continue;
        edges.push(`<bpmndi:BPMNEdge id="${match[1]}_di" bpmnElement="${match[1]}"><di:waypoint x="${source.x + source.width}" y="${source.y + source.height / 2}" /><di:waypoint x="${target.x}" y="${target.y + target.height / 2}" /></bpmndi:BPMNEdge>`);
    }
    let namespaced = xml;
    if (!namespaced.includes("xmlns:bpmndi="))
        namespaced = namespaced.replace(/<bpmn:definitions\b/, `<bpmn:definitions xmlns:bpmndi="${BPMNDI_NAMESPACE}"`);
    if (!namespaced.includes("xmlns:dc="))
        namespaced = namespaced.replace(/<bpmn:definitions\b/, `<bpmn:definitions xmlns:dc="${DC_NAMESPACE}"`);
    if (!namespaced.includes("xmlns:di="))
        namespaced = namespaced.replace(/<bpmn:definitions\b/, `<bpmn:definitions xmlns:di="${DI_NAMESPACE}"`);
    const diagram = `<bpmndi:BPMNDiagram id="BPMNDiagram_Auto"><bpmndi:BPMNPlane id="BPMNPlane_Auto" bpmnElement="${processMatch[1]}">${shapes}${edges.join("")}</bpmndi:BPMNPlane></bpmndi:BPMNDiagram>`;
    return namespaced.replace("</bpmn:definitions>", `${diagram}</bpmn:definitions>`);
};
